import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { Header } from "../components/Header";
import { ClubDiagnostic } from "../components/ClubDiagnostic";
import { useUserDetail } from "../hooks/useUserDetail";
import { formatDetailDate, getStatusClass } from "../lib/format";

export const Route = createFileRoute("/users/$customerId")({
  component: UserDetailPage,
});

function UserDetailPage() {
  const { customerId } = Route.useParams();
  const navigate = useNavigate();
  const { data, isLoading, error } = useUserDetail(customerId);

  const user = data?.user;
  const orders = data?.orders ?? [];
  const stats = data?.stats ?? { totalOrders: 0, totalRolls: 0, ordersWithPrints: 0 };

  useEffect(() => {
    if (user) document.title = (user["full-name"] || "Customer") + " - TPFL All Orders";
  }, [user]);

  const notFound = (data && data.error) || (!isLoading && !user);
  const cid = (user && (user["customer-id"] as string)) || "";

  return (
    <>
      <Header variant="detail" />
      <div className="detail-breadcrumbs">
        <a href="/">Search</a>
        <span className="breadcrumb-sep"><i className="ph ph-caret-right" /></span>
        <span className="breadcrumb-current" id="breadcrumbName">
          {isLoading ? "Loading..." : user?.["full-name"] || "Unknown Customer"}
        </span>
      </div>

      <div className="detail-page-content" id="detailContent">
        {isLoading ? (
          <div className="loading-message">Loading customer details...</div>
        ) : error || notFound ? (
          <div className="loading-message">{error ? "Error loading customer data" : "Customer not found"}</div>
        ) : (
          <>
            <div className="user-header-card">
              <div className="user-header-content">
                <div className="user-primary-info">
                  <h1>{user["full-name"] || "Unknown Customer"}</h1>
                  {user.email && (
                    <a href={`mailto:${user.email}`} className="user-email-link">
                      {user.email}
                    </a>
                  )}
                  <div className="user-badges">
                    {user["club-member"] ? <span className="badge badge-club">Club Member</span> : null}
                    <span className="badge badge-orders">{stats.totalOrders} Orders</span>
                  </div>
                  {(cid.startsWith("cus_") || user.email) && (
                    <div className="action-buttons">
                      {cid.startsWith("cus_") && (
                        <a href={`https://dashboard.stripe.com/customers/${cid}`} target="_blank" rel="noreferrer" className="action-button stripe-action">
                          💳 View in Stripe
                        </a>
                      )}
                      {user.email && (
                        <a href={`mailto:${user.email}`} className="action-button email-action">
                          ✉️ Send Email
                        </a>
                      )}
                    </div>
                  )}
                </div>
                <div className="user-stats-grid">
                  <div className="stat-box"><div className="stat-value">{stats.totalOrders}</div><div className="stat-label">Total Orders</div></div>
                  <div className="stat-box"><div className="stat-value">{stats.totalRolls}</div><div className="stat-label">Total Rolls</div></div>
                  <div className="stat-box"><div className="stat-value">{stats.ordersWithPrints}</div><div className="stat-label">Orders with Prints</div></div>
                </div>
              </div>
            </div>

            {user["club-member"] ? <ClubDiagnostic customerId={customerId} /> : null}

            <div className="order-history-section">
              <h2>Order History</h2>
              {orders.length === 0 ? (
                <div className="loading-message">No orders found</div>
              ) : (
                orders.map((order: any, i: number) => {
                  const twinChecks = order.allOrders?.["Twin Check Stickers"] || "N/A";
                  const rolls =
                    order.allOrders?.["amount-of-rolls"] ||
                    (order.filmDrop?.["color-35"] || 0) +
                      (order.filmDrop?.["bw-35"] || 0) +
                      (order.filmDrop?.["color-120"] || 0) +
                      (order.filmDrop?.["bw-120"] || 0) ||
                    0;
                  const rollTypes = order.allOrders?.["roll-types"] || "N/A";
                  const prints = order.allOrders?.prints || "None";
                  return (
                    <div
                      className="order-card"
                      key={i}
                      onClick={() => navigate({ to: "/orders/$invoiceId", params: { invoiceId: String(order.invoiceId) } })}
                    >
                      <div className="order-card-header">
                        <span className="order-date">{formatDetailDate(order.timestamp)}</span>
                        <span className={`status-badge ${getStatusClass(order.status)}`}>{order.status || "pending"}</span>
                      </div>
                      <div className="order-card-body">
                        <div className="order-field"><div className="order-field-label">Twin Check</div><div className="order-field-value">{twinChecks}</div></div>
                        <div className="order-field"><div className="order-field-label">Rolls</div><div className="order-field-value">{rolls}</div></div>
                        <div className="order-field"><div className="order-field-label">Type</div><div className="order-field-value">{rollTypes}</div></div>
                        <div className="order-field"><div className="order-field-label">Prints</div><div className="order-field-value">{prints}</div></div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
}
